import _classCallCheck from "/home/runner/work/soroban-rpc-api/soroban-rpc-api/build/docs/gatsby/node_modules/@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "/home/runner/work/soroban-rpc-api/soroban-rpc-api/build/docs/gatsby/node_modules/@babel/runtime/helpers/esm/createClass";
import "core-js/modules/es.typed-array.sort.js";

/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
export var MAX_FOLDING_REGIONS = 0xFFFF;
export var MAX_LINE_NUMBER = 0xFFFFFF;
var MASK_INDENT = 0xFF000000;
export var FoldingRegions = /*#__PURE__*/function () {
  function FoldingRegions(startIndexes, endIndexes, types) {
    _classCallCheck(this, FoldingRegions);

    if (startIndexes.length !== endIndexes.length || startIndexes.length > MAX_FOLDING_REGIONS) {
      throw new Error('invalid startIndexes or endIndexes size');
    }

    this._startIndexes = startIndexes;
    this._endIndexes = endIndexes;
    this._collapseStates = new Uint32Array(Math.ceil(startIndexes.length / 32));
    this._types = types;
    this._parentsComputed = false;
  }

  _createClass(FoldingRegions, [{
    key: "ensureParentIndices",
    value: function ensureParentIndices() {
      var _this = this;

      if (!this._parentsComputed) {
        this._parentsComputed = true;
        var parentIndexes = [];

        var isInsideLast = function isInsideLast(startLineNumber, endLineNumber) {
          var index = parentIndexes[parentIndexes.length - 1];
          return _this.getStartLineNumber(index) <= startLineNumber && _this.getEndLineNumber(index) >= endLineNumber;
        };

        for (var i = 0, len = this._startIndexes.length; i < len; i++) {
          var startLineNumber = this._startIndexes[i];
          var endLineNumber = this._endIndexes[i];

          if (startLineNumber > MAX_LINE_NUMBER || endLineNumber > MAX_LINE_NUMBER) {
            throw new Error('startLineNumber or endLineNumber must not exceed ' + MAX_LINE_NUMBER);
          }

          while (parentIndexes.length > 0 && !isInsideLast(startLineNumber, endLineNumber)) {
            parentIndexes.pop();
          }

          var parentIndex = parentIndexes.length > 0 ? parentIndexes[parentIndexes.length - 1] : -1;
          parentIndexes.push(i);
          this._startIndexes[i] = startLineNumber + ((parentIndex & 0xFF) << 24);
          this._endIndexes[i] = endLineNumber + ((parentIndex & 0xFF00) << 16);
        }
      }
    }
  }, {
    key: "length",
    get: function get() {
      return this._startIndexes.length;
    }
  }, {
    key: "getStartLineNumber",
    value: function getStartLineNumber(index) {
      return this._startIndexes[index] & MAX_LINE_NUMBER;
    }
  }, {
    key: "getEndLineNumber",
    value: function getEndLineNumber(index) {
      return this._endIndexes[index] & MAX_LINE_NUMBER;
    }
  }, {
    key: "getType",
    value: function getType(index) {
      return this._types ? this._types[index] : undefined;
    }
  }, {
    key: "hasTypes",
    value: function hasTypes() {
      return !!this._types;
    }
  }, {
    key: "isCollapsed",
    value: function isCollapsed(index) {
      var arrayIndex = index / 32 | 0;
      var bit = index % 32;
      return (this._collapseStates[arrayIndex] & 1 << bit) !== 0;
    }
  }, {
    key: "setCollapsed",
    value: function setCollapsed(index, newState) {
      var arrayIndex = index / 32 | 0;
      var bit = index % 32;
      var value = this._collapseStates[arrayIndex];

      if (newState) {
        this._collapseStates[arrayIndex] = value | 1 << bit;
      } else {
        this._collapseStates[arrayIndex] = value & ~(1 << bit);
      }
    }
  }, {
    key: "toRegion",
    value: function toRegion(index) {
      return new FoldingRegion(this, index);
    }
  }, {
    key: "getParentIndex",
    value: function getParentIndex(index) {
      this.ensureParentIndices();
      var parent = ((this._startIndexes[index] & MASK_INDENT) >>> 24) + ((this._endIndexes[index] & MASK_INDENT) >>> 16);

      if (parent === MAX_FOLDING_REGIONS) {
        return -1;
      }

      return parent;
    }
  }, {
    key: "contains",
    value: function contains(index, line) {
      return this.getStartLineNumber(index) <= line && this.getEndLineNumber(index) >= line;
    }
  }, {
    key: "findIndex",
    value: function findIndex(line) {
      var low = 0,
          high = this._startIndexes.length;

      if (high === 0) {
        return -1; // no children
      }

      while (low < high) {
        var mid = Math.floor((low + high) / 2);

        if (line < this.getStartLineNumber(mid)) {
          high = mid;
        } else {
          low = mid + 1;
        }
      }

      return low - 1;
    }
  }, {
    key: "findRange",
    value: function findRange(line) {
      var index = this.findIndex(line);

      if (index >= 0) {
        var endLineNumber = this.getEndLineNumber(index);

        if (endLineNumber >= line) {
          return index;
        }

        index = this.getParentIndex(index);

        while (index !== -1) {
          if (this.contains(index, line)) {
            return index;
          }

          index = this.getParentIndex(index);
        }
      }

      return -1;
    }
  }, {
    key: "toString",
    value: function toString() {
      var res = [];

      for (var i = 0; i < this.length; i++) {
        res[i] = "[".concat(this.isCollapsed(i) ? '+' : '-', "] ").concat(this.getStartLineNumber(i), "/").concat(this.getEndLineNumber(i));
      }

      return res.join(', ');
    }
  }]);

  return FoldingRegions;
}();
export var FoldingRegion = /*#__PURE__*/function () {
  function FoldingRegion(ranges, index) {
    _classCallCheck(this, FoldingRegion);

    this.ranges = ranges;
    this.index = index;
  }

  _createClass(FoldingRegion, [{
    key: "startLineNumber",
    get: function get() {
      return this.ranges.getStartLineNumber(this.index);
    }
  }, {
    key: "endLineNumber",
    get: function get() {
      return this.ranges.getEndLineNumber(this.index);
    }
  }, {
    key: "regionIndex",
    get: function get() {
      return this.index;
    }
  }, {
    key: "parentIndex",
    get: function get() {
      return this.ranges.getParentIndex(this.index);
    }
  }, {
    key: "isCollapsed",
    get: function get() {
      return this.ranges.isCollapsed(this.index);
    }
  }, {
    key: "containedBy",
    value: function containedBy(range) {
      return range.startLineNumber <= this.startLineNumber && range.endLineNumber >= this.endLineNumber;
    }
  }, {
    key: "containsLine",
    value: function containsLine(lineNumber) {
      return this.startLineNumber <= lineNumber && lineNumber <= this.endLineNumber;
    }
  }]);

  return FoldingRegion;
}();